import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ConfirmationService } from 'primeng/api';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { NavMenuService } from 'src/app/components/core/components/navmenu/navmenu.service';
import { UnidadInspeccionService } from '../../unidad-inspeccion.service';
import { EnumCodigosTwTcAcciones } from 'src/app/components/_importacion/solicitudes/types/enum';
import { catchError, of, tap } from 'rxjs';
import { InspeccionUiService } from '../../inspeccion-ui.service';
import { TwInspeccionCamViewModel } from '../../inspeccion.types';
import {
    InspeccionPeriodoCamViewModel,
    ListaInspeccionPeriodoViewModel,
} from 'src/app/components/_mantenimiento/campanias/periodos/inspecciones/inspecciones.types';
import { CampaniasGlobalService } from '../../../services/global-campanias.service';
import { TwUnidadInspeccionViewModelExtended } from '../../unidad-inspeccion-datosbasicos.types';

@Component({
    selector: 'app-inspeccion-ui-wrapper',
    templateUrl: './inspeccion-ui-wrapper.component.html',
    styleUrls: ['./inspeccion-ui-wrapper.component.scss'],
})
export class InspeccionUiWrapperComponent extends ComponentBase implements OnInit {
    visibilitySpinner = false;
    formGroup: FormGroup;
    unidadInspeccion: TwUnidadInspeccionViewModelExtended;
    inspecciones: TwInspeccionCamViewModel[];
    listaInspeccionPeriodo: InspeccionPeriodoCamViewModel[];
    periodoEnPlazoVigente = false;

    private listUrl = '/campanias/unidadesinspeccion';
    private inspeccionUIDetailUrl = '/campanias/unidadinspeccion/inspecciones';

    private activatedRoute$ = this.activatedRoute.paramMap.subscribe(params => {
        const idUi: any = params.get('idUi');
        if (idUi) {
            this.getUnidadInspeccion(idUi);
            this.getInspecciones(idUi);
        }
    });

    title = { plural: 'Inspecciones', singular: 'Inspección' };

    constructor(
        private fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
        private router: Router,
        private unidadInspeccionService: UnidadInspeccionService,
        private globalCampaniasService: CampaniasGlobalService,
        private inspeccionUIService: InspeccionUiService,
        private navMenuService: NavMenuService,
        private confirmationService: ConfirmationService,
        protected authService: AuthService,
        protected notificationService: NotificationService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.preparePresentation();
    }

    private preparePresentation(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.fb.group({
            Campania: [null],
            Periodo: [null],
            DescripcionComunidad: [null],
            EntidadAuditora: [null],
            TipoRecinto: [null], // Tipo de Solicitud
            Operador: [null],
        });
        this.form = this.formGroup;
    }

    /**
     * Se cargan los datos de la unidad de inspección y sus recintos "PI-A-Ed" asociados
     * Una vez obtenida, establece el valor para la propiedad readonly a partir de la información del usuario y los permisos de edición.
     * Después invoca al servicio de obtención de inspecciones de la U.I.
     * @param id number
     */
    private getUnidadInspeccion(id: number): void {
        this.unidadInspeccionService
            .getUI(id)
            .pipe(
                tap((udInspeccion: TwUnidadInspeccionViewModelExtended) => {
                    // Una UI existente deberá tener al menos un recinto "PI-A-Ed".
                    if (!udInspeccion?.FirstRecinto) {
                        // No debería pasar por aquí, porque cuando se eliminan todos los recintos "PI-A-Ed" de una UI, se elimina además la UI
                        return;
                    }
                    this.unidadInspeccion = udInspeccion;
                    this.periodoEnPlazoVigente = this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente;
                    // Cargamos la lista de tipos de inspecciones del periodo
                    if (this.unidadInspeccion?.FirstRecinto?.IdPeriodo) {
                        this.getListaTiposInspeccionesPeriodo(this.unidadInspeccion?.FirstRecinto?.IdPeriodo);
                    } else {
                        // @author : por aquí no debería pasar
                        console.error('Primer Recinto de Unidad de Inspección sin periodo', this.unidadInspeccion?.IdUi);
                    }

                    this.navMenuService.updateMenuServiceForUdInspeccion(
                        udInspeccion.IdUi!,
                        udInspeccion.IdEstadoUi!,
                        udInspeccion.FirstRecinto.IdTipoSolicitudCam || 0,
                        udInspeccion.PeriodoCertificaKgs,
                    );
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Se obtiene la lista de tipos de inspecciones del periodo.
     * A partir de ella se construye la lista que se utilizará en el desplegable.
     * @param periodoId: number
     * @return void
     */
    private getListaTiposInspeccionesPeriodo(periodoId: number): void {
        if (!periodoId) {
            return;
        }
        this.listaInspeccionPeriodo = [];

        this.globalCampaniasService
            .getListaInspeccionesDePeriodo(periodoId)
            .pipe(
                tap((results: ListaInspeccionPeriodoViewModel) => {
                    if (results) {
                        this.listaInspeccionPeriodo = results?.InspeccionPeriodoCollection;
                    }
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Carga la lista de inspecciones de la U.I.
     * @param idUI number
     */
    private getInspecciones(idUI: number): void {
        this.inspeccionUIService
            .getListByUI(idUI)
            .pipe(
                tap((inspecciones: TwInspeccionCamViewModel[]) => {
                    this.inspecciones = inspecciones;
                }),
                catchError(error => {
                    this.catchServerError(error);
                    this.formGroup.disable();
                    return of(null);
                }),
            )
            .subscribe();
    }

    onBack(): void {
        this.router.navigate([this.listUrl]);
    }

    /**
     * Redigirimos al usuario a la pantalla de edición de la UI, para la selección de recintos.
     */
    onClickAdd(): void {
        this.router.navigate([this.inspeccionUIDetailUrl, this.unidadInspeccion?.IdUi, 'create']);
    }

    onView(inspeccion: TwInspeccionCamViewModel): void {
        this.router.navigate([this.inspeccionUIDetailUrl, this.unidadInspeccion?.IdUi, 'detail', inspeccion.Id]);
    }

    private doReload(): void {
        this.getUnidadInspeccion(this.unidadInspeccion.IdUi!);
        this.getInspecciones(this.unidadInspeccion.IdUi!);
    }

    onDelete(idInspeccion: number): void {
        if (!idInspeccion) return;
        this.confirmationService.confirm({
            message: '¿Desea eliminar la inspección?',
            accept: () => {
                this.eliminaInspeccion(idInspeccion);
            },
        });
    }

    private eliminaInspeccion(id: number): void {
        this.inspeccionUIService
            .delete(id)
            .pipe(
                tap(() => {
                    this.addSuccessMessage('Inspección eliminada con éxito');
                    this.doReload();
                }),
                catchError(error => {
                    this.catchServerError(error);
                    return of(null);
                }),
            )
            .subscribe();
    }

    /**
     * Este método indica si se pueden añadir inspecciones a la unidad de inspección.
     * Si el objeto unidadInspeccion no se ha cargado, no se le habilita el permiso.
     * Además se comprueba que el usuario (por perfil) tenga los permisos necesarios.
     * @return boolean
     */
    get hasAddingAccess(): boolean {
        if (!this.unidadInspeccion) return false;

        const hasAccess =
            this.loggedInUser?.getAction(
                EnumCodigosTwTcAcciones.ADD_INSPECCION_UI,
                this.unidadInspeccion?.IdEstadoUi,
                this.unidadInspeccion.FirstRecinto.IdTipoSolicitudCam,
            ).Editable || false;
        return hasAccess;
    }

    get actionNotAllowed(): boolean {
        return this.visibilitySpinner || !this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente || this.listaInspeccionPeriodo?.length === 0;
    }

    get readonly(): boolean {
        return this.unidadInspeccion?.FirstRecinto?.PeriodoEnPlazoVigente === false;
    }
}
